import { useCallback, useEffect, useState } from "react";

export const useShopifyScript = ({
  buttonSelector = "button[data-shopify-button]",
  autoRegister = true,
  universalRetailerId = "",
  scriptUrl = "https://adhawk-marketplace-assets.s3-us-west-1.amazonaws.com/v4/index-factory.js",
  cssUrl = "https://adhawk-marketplace-assets.s3-us-west-1.amazonaws.com/index.css",
  onScriptLoad = () => {},
  onScriptError = () => {},
} = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadStyle = useCallback(() => {
    if (!cssUrl) return;

    const existingLink = document.querySelector(`link[href="${cssUrl}"]`);
    if (existingLink) {
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = cssUrl;
    document.head.appendChild(link);
  }, [cssUrl]);

  const registerButton = useCallback((button) => {
    if (!button || !window?.EzCommerce?.shopify) {
      console.warn(
        "EzCommerce shopify API not available for button registration.",
      );
      return;
    }

    if (
      button.classList.contains("shopify-buy-frame") ||
      button.getAttribute("data-shopify-registered") === "true"
    ) {
      return;
    }

    try {
      window.EzCommerce.shopify.createUIComponents(
        button,
        true,
        button.dataset,
      );
      window.EzCommerce.populateNotesFieldData(button.dataset);
      button.setAttribute("data-shopify-registered", "true");
    } catch (error) {
      console.error("Error registering Shopify button:", error);
    }
  }, []);

  const reregisterButtons = useCallback(() => {
    if (!window?.EzCommerce?.shopify) return;

    const buttons = document.querySelectorAll(buttonSelector);
    buttons.forEach((button) => registerButton(button));
  }, [buttonSelector, registerButton]);

  const loadScript = useCallback(() => {
    if (isLoading || isLoaded) return;

    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      if (window?.EzCommerce) {
        setIsLoaded(true);
      }
      return;
    }

    setIsLoading(true);

    loadStyle();

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = scriptUrl;
    script.id = "adhawk-shopify-camilyo-marketplace";

    script.onload = () => {
      setIsLoading(false);
      try {
        if (typeof window?.EzCommerceGenerator === "function") {
          window.EzCommerceGenerator({
            universalRetailerId: universalRetailerId || "",
          })();
          setIsLoaded(true);
          onScriptLoad();
        }
      } catch (error) {
        console.error("Error initializing EzCommerce script:", error);
        onScriptError(error);
      }
    };

    script.onerror = () => {
      setIsLoading(false);
      const error = new Error(`Failed to load script: ${scriptUrl}`);
      console.error(error);
      onScriptError(error);
    };

    document.head.appendChild(script);
  }, [
    isLoading,
    isLoaded,
    scriptUrl,
    universalRetailerId,
    onScriptError,
    onScriptLoad,
  ]);

  useEffect(() => {
    if (isLoaded && autoRegister) {
      reregisterButtons();
    }
  }, [isLoaded, autoRegister, reregisterButtons]);

  return {
    isLoaded,
    isLoading,
    loadScript,
    reregisterButtons,
    registerButton,
  };
};
